/**
 * CapabilitiesModal — modal compartido de configuración de capacidades.
 *
 * ISS-572 / REQ-CAP-002 §4/§5: el listado editable vive en un modal (patrón
 * "Skills scope"): las tres superficies (agente, hive, Settings→Chat) aportan
 * el control por fila y el footer de guardado; el modal aporta el contenedor
 * con scroll interno, cabecera y protección de cambios sin guardar.
 */

import type { ReactNode } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useI18n } from '@/i18n';
import { CapabilityRow } from './CapabilityRow';
import type { CapabilityCatalogEntry, EffectiveCapability } from './types';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  catalog: CapabilityCatalogEntry[];
  effective: Record<string, EffectiveCapability>;
  /** Control por fila de la superficie (check, tristate…). */
  renderControl: (entry: CapabilityCatalogEntry) => ReactNode;
  /** Panel de settings por fila (formulario editable o resumen). */
  renderExtra?: (entry: CapabilityCatalogEntry) => ReactNode;
  footer?: ReactNode;
  /** Hay cambios sin guardar (protección de descarte del primitive). */
  dirty?: boolean;
}

export function CapabilitiesModal({
  open,
  onOpenChange,
  title,
  description,
  catalog,
  effective,
  renderControl,
  renderExtra,
  footer,
  dirty = false,
}: Props) {
  const { t } = useI18n();
  const discardLabels = {
    title: t('common.discardChanges.title') || 'Discard changes?',
    message:
      t('common.discardChanges.message') ||
      'You have unsaved changes. If you close now, they will be lost.',
    confirm: t('common.discardChanges.discard') || 'Discard',
    cancel: t('common.discardChanges.keepEditing') || 'Keep editing',
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (next !== open) onOpenChange(next);
      }}
    >
      <DialogContent
        className="sm:max-w-3xl"
        dirty={dirty}
        discardTitle={discardLabels.title}
        discardMessage={discardLabels.message}
        discardConfirmLabel={discardLabels.confirm}
        discardCancelLabel={discardLabels.cancel}
      >
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>

        <div className="max-h-[60vh] space-y-3 overflow-y-auto py-2 pr-1">
          {catalog.map((entry) => (
            <CapabilityRow
              key={entry.slug}
              entry={entry}
              effective={effective[entry.slug]}
              control={renderControl(entry)}
              extra={renderExtra?.(entry)}
            />
          ))}
        </div>

        {footer && <DialogFooter>{footer}</DialogFooter>}
      </DialogContent>
    </Dialog>
  );
}
